import { type AxiosRequestHeaders } from 'axios';
import { getLaunchProfileName, getLaunchSurface } from '../launchAttribution';
import { getDeviceId } from './deviceId';
import { getDeviceSessionId } from './deviceSessionId';

/**
 * Adds the FE↔BE tracing headers (DEV-11834) to a request's headers in place.
 *
 * Sets:
 *   - `X-Device-Id` — stable per-browser-profile identifier (see `deviceId.ts`)
 *   - `X-Device-Session-Id` — per-tab session identifier (see `deviceSessionId.ts`)
 *
 * `user_ref_id` is deliberately NOT sent as a request header. It is a
 * client-controlled value the backend cannot trust for attribution, so the BE
 * derives it from the decoded access token instead (token wins). Both device
 * identifiers are correlation-only metadata with no authorization meaning, so
 * client control of them is acceptable. See ml-scribe#10029 and Ian Craig's
 * review thread on PR #10826.
 *
 * Also sets two optional launch headers, both scoped to the launched session
 * (see `launchAttribution.ts`): `X-Ehr-Profile-Name` carrying the EHR launch
 * profile, and `X-Heidi-Surface` naming the surface when only the client can
 * tell it apart (Haiku and SMART-on-FHIR launches both reach the backend as
 * plain web).
 *
 * Designed to be cheap and synchronous so it can run on every axios request
 * interceptor without blocking. Never throws.
 */
export function applyTracingHeaders(headers: AxiosRequestHeaders | undefined): void {
  if (!headers) return;
  headers['X-Device-Id'] = getDeviceId();
  headers['X-Device-Session-Id'] = getDeviceSessionId();
  const ehrProfileName = getLaunchProfileName();
  if (ehrProfileName) headers['X-Ehr-Profile-Name'] = ehrProfileName;
  const surface = getLaunchSurface();
  if (surface) headers['X-Heidi-Surface'] = surface;
}
